import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { TBankOffers } from '@/components/App/NewOrder/offer-step';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { getNewestValues } from '@/services';
import type { TFileAttached } from '@/types/financing';
import type { TAutoCredLeasingResponse } from '@/types/newOrder';
import type { TFinancingStepStore } from '@/types/store';

import { createAsyncThunk } from './create-async-thunk';

const initialState: TFinancingStepStore = {
  selectedBank: null,
  selectedLeasing: null,
  attachedFiles: [],
  shouldUpdateFiles: true,
  isVehicleInfoValid: false,
  isGrnFactoryCheckPassed: true,
  shouldUpdateBankInfo: undefined,
  instructionsText: { isSuccess: false, main: '', secondary: '' },
  isEuBankOfflineProcess: false,
  isBitrixDocumentsAccepted: true
};

export const getAttachedFilesByReqId = createAsyncThunk(
  'financingStep/getAttachedFilesByReqId',
  async (reqId: string) => {
    return autoCreditApi.getAttachedFilesByReqId(reqId);
  }
);

export const financingStepSlice = createSlice({
  name: 'financingStep',
  initialState,
  reducers: {
    onSetSelectedBank: (state, action: PayloadAction<TBankOffers | null>) => {
      state.selectedBank = action.payload;
    },
    onSetSelectedLeasing: (
      state,
      action: PayloadAction<TAutoCredLeasingResponse | null>
    ) => {
      state.selectedLeasing = action.payload;
    },
    onSetFinancingStoreToInitialState: () => {
      return initialState;
    },
    onSetSelectedBankToInitial: (state) => {
      state.selectedBank = null;
    },
    setShouldUpdateFiles: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdateFiles = action.payload;
    },
    setIsVehicleInfoValid: (state, action: PayloadAction<boolean>) => {
      state.isVehicleInfoValid = action.payload;
    },
    setShouldUpdateBankInfo: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.shouldUpdateBankInfo = action.payload;
    },
    setInstructionsText: (
      state,
      action: PayloadAction<{
        isSuccess: boolean;
        main: string;
        secondary?: string;
      }>
    ) => {
      state.instructionsText = action.payload;
    },
    setIsBitrixDocumentsAccepted: (state, action: PayloadAction<boolean>) => {
      state.isBitrixDocumentsAccepted = action.payload;
    },
    setIsEuBankOfflineProcess: (state, action: PayloadAction<boolean>) => {
      state.isEuBankOfflineProcess = action.payload;
    },
    setIsGrnFactoryCheckPassed: (state, action: PayloadAction<boolean>) => {
      state.isGrnFactoryCheckPassed = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getAttachedFilesByReqId.fulfilled, (state, action) => {
      // Игнорим типы, для которых важно иметь несколько файлов
      const ignoredTypes = ['grnAltyn'];

      const filteredFiles = action.payload.filter(
        (item) => item.type && item.name
      );
      const filesWithIgnoredTypes: TFileAttached[] = [];
      const filesWithoutIgnoredTypes: TFileAttached[] = [];

      filteredFiles.forEach((item) => {
        if (ignoredTypes.includes(item.type)) {
          filesWithIgnoredTypes.push(item);
        } else {
          filesWithoutIgnoredTypes.push(item);
        }
      });

      state.attachedFiles = getNewestValues(
        filesWithoutIgnoredTypes,
        'type',
        'create_date'
      ).concat(filesWithIgnoredTypes);
    });
  }
});

export const {
  onSetSelectedBank,
  onSetSelectedLeasing,
  onSetFinancingStoreToInitialState,
  onSetSelectedBankToInitial,
  setShouldUpdateFiles,
  setIsVehicleInfoValid,
  setShouldUpdateBankInfo,
  setInstructionsText,
  setIsBitrixDocumentsAccepted,
  setIsEuBankOfflineProcess,
  setIsGrnFactoryCheckPassed
} = financingStepSlice.actions;
